import { Flex, Text, Link, Box } from '@chakra-ui/react';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import { setFocusedField } from '../../../store/redux/slices/template.slice';
import {
  useContext,
  useState,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import {
  STAMP_BANNER_LABELS,
  STAMP_VALUE_LABELS,
} from './utils/stampCardConstants';
import {
  BUILDER_DEVICE_TAB as TABS,
  BUILDER_PASS_TAB as PASS_TABS,
  type BuilderDeviceTab,
  type BuilderPassTab,
} from './utils/templateBuilderTabs';
import { ENHANCED_EVENT_TICKET_IMAGE_LABELS } from './utils/enhancedEventTicketLabels';
import {
  APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE,
  APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE,
  ENHANCED_EVENT_TICKET_SECTION_ID,
  FEATURED_ACTIONS_SECTION_ID,
} from './utils/appleEnhancedDesignConstants';
import { FEATURED_ACTION_SLOT_KEYS } from './utils/featuredActionTypes';
import type { FeaturedActionSlotKey } from '../../../store/types/template';
import {
  MAIN_TABS,
  navigateToSection,
  type NavigationConfig,
} from './utils/sectionNavigation';

const capitalizeFirst = (str: string): string => {
  return str[0].toUpperCase() + str.slice(1);
};

const camelCaseToTitleCase = (str: string): string => {
  return str.replace(/([A-Z])/g, ' $1').toLowerCase();
};

interface ErrorLinkProps {
  label: string;
  errorMessage: string;
  onNavigate: () => void;
}

const ErrorLink = ({ label, errorMessage, onNavigate }: ErrorLinkProps) => {
  return (
    <li>
      <Link
        onClick={(e) => {
          e.preventDefault();
          onNavigate();
        }}
        textDecoration="underline"
        cursor="pointer"
        color="#FF1010"
        _hover={{ textDecoration: 'none' }}
      >
        [{label}]
      </Link>{' '}
      {errorMessage}
    </li>
  );
};

const ErrorsBanner = ({
  setSelectedTab,
  setSelectedPassTab,
  setSelectedMainTabIndex,
  setPendingExpandLocationIndex,
}: {
  setSelectedTab: Dispatch<SetStateAction<BuilderDeviceTab>>;
  setSelectedPassTab: Dispatch<SetStateAction<BuilderPassTab>>;
  setSelectedMainTabIndex: (index: number) => void;
  setPendingExpandLocationIndex?: Dispatch<SetStateAction<number | null>>;
}) => {
  const dispatch = useAppDispatch();
  const { state: meState } = useContext(MeContext) as { state: MeContextState };
  const expressivePassRolloutEnabled =
    meState?.expressivePassRolloutEnabled === true;
  const errors = useAppSelector((state) => state.templateErrors);
  const stampFeedbackTypes = useAppSelector(
    (state) => state.stampFeedbackTypes
  );
  const [displayedErrors, setDisplayedErrors] = useState<typeof errors>({});

  useEffect(() => {
    const errorCount = Object.keys(errors).length;

    if (errorCount === 0) {
      setDisplayedErrors({});
      return;
    }

    const timeoutId = setTimeout(() => {
      setDisplayedErrors(errors);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [errors]);

  const errorCount = Object.keys(displayedErrors).length;
  if (errorCount === 0) return null;

  const navigate = (config: NavigationConfig) =>
    navigateToSection(
      {
        setSelectedMainTabIndex,
        setSelectedTab,
        setSelectedPassTab,
        setPendingExpandLocationIndex,
      },
      config
    );

  const renderErrorItem = (error: string) => {
    const errorMessage = displayedErrors[error];

    // Issuance form errors
    if (error.startsWith('form.')) {
      let label = 'Form';
      let sectionId = 'form-details-section';
      let displayMessage = '';
      if (error === 'form.slug') {
        label = 'Form name';
        sectionId = 'form-details-section';
        if (errorMessage.includes('is required')) {
          displayMessage = 'is required';
        } else if (errorMessage.includes('must be unique')) {
          displayMessage = 'must be unique';
        }
      } else if (error.startsWith('form.formFields.')) {
        const match = error.match(/form\.formFields\.(\d+)/);
        const fieldIndex = match ? parseInt(match[1], 10) : 0;
        label = `Form field ${fieldIndex + 1}`;
        sectionId = `form-field-${fieldIndex}`;
      } else if (
        error.includes('backgroundColor') ||
        error.includes('componentFillColor') ||
        error.includes('componentFontColor') ||
        error.includes('buttonCtaColor') ||
        error === 'form.buttonText' ||
        error.includes('buttonColor') ||
        error.includes('formLogoImage') ||
        error.includes('formFaviconImage')
      ) {
        const fieldMap: Record<string, string> = {
          'form.backgroundColor': 'Background colour',
          'form.componentFillColor': 'Component colour',
          'form.componentFontColor': 'Font colour',
          'form.buttonCtaColor': 'Button background colour',
          'form.buttonText': 'Button text',
          'form.buttonColor': 'Button background colour',
        };
        label = fieldMap[error] || 'Design';
        sectionId = 'form-design-section';
        displayMessage = 'must be a valid hex colour';
        if (error === 'form.buttonText') {
          displayMessage = 'is required';
        }
      } else if (error.includes('queryParams')) {
        const match = error.match(/form\.queryParams\.(\d+)/);
        const paramIndex = match ? parseInt(match[1], 10) : 0;
        label = match ? `Query parameter ${paramIndex + 1}` : 'Query parameter';
        sectionId = `query-param-${paramIndex}`;
      } else if (
        error.includes('footerContent') ||
        error.includes('footerColor') ||
        error.includes('footerEnabled')
      ) {
        label = 'Footer';
        sectionId = 'custom-footer-section';
        if (error === 'form.footerContent') {
          label = 'Footer text';
          displayMessage = 'is required';
        } else if (error === 'form.footerColor') {
          label = 'Footer colour';
          displayMessage = 'must be a valid hex color';
        }
      }

      return (
        <ErrorLink
          key={error}
          label={label}
          errorMessage={
            displayMessage || errorMessage?.toLowerCase() || 'has an error'
          }
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.ISSUANCE_FORM,
              sectionId,
            })
          }
        />
      );
    }

    // Template details errors
    if (
      error === 'templateName' ||
      error === 'description' ||
      error === 'centralTitle' ||
      error === 'transitType'
    ) {
      const labels: Record<string, string> = {
        templateName: 'Template name',
        description: 'Description',
        centralTitle: 'Central title',
        transitType: 'Transit type',
      };
      let displayMessage = 'is required';
      if (
        error === 'templateName' &&
        errorMessage !== 'Template name is required'
      ) {
        if (errorMessage.includes('must be unique')) {
          displayMessage = 'must be unique';
        } else {
          displayMessage = errorMessage.toLowerCase();
        }
      }
      return (
        <ErrorLink
          key={error}
          label={labels[error]}
          errorMessage={displayMessage}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FRONT,
              sectionId: 'template-details-section',
            })
          }
        />
      );
    }

    // Notification header
    if (error === 'notificationHeader') {
      return (
        <ErrorLink
          key={error}
          label="Notification header"
          errorMessage="is required"
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FRONT,
              sectionId: !stampFeedbackTypes?.includes(
                'sendPushNotification'
              )
                ? 'notifications-section'
                : 'push-notifications-section',
            })
          }
        />
      );
    }

    if (error.startsWith(`${APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE}.`)) {
      const parts = error.split('.');
      const field = parts[2] as 'artwork' | 'secondaryLogo' | 'venueMap';
      const isDimensionError = parts.length === 4 && parts[3] === 'dimensions';
      const isArtwork = field === 'artwork';
      return (
        <ErrorLink
          key={error}
          label={
            ENHANCED_EVENT_TICKET_IMAGE_LABELS[field] || capitalizeFirst(field)
          }
          errorMessage={isDimensionError ? errorMessage : 'is required'}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.APPLE,
              passTab: PASS_TABS.FRONT,
              sectionId: isArtwork
                ? 'design-section'
                : ENHANCED_EVENT_TICKET_SECTION_ID,
            })
          }
        />
      );
    }

    if (error.startsWith(APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE)) {
      const slot = error.split('.')[2];
      const slotNumber = FEATURED_ACTION_SLOT_KEYS.indexOf(
        slot as FeaturedActionSlotKey
      );
      return (
        <ErrorLink
          key={error}
          label={
            slotNumber === -1
              ? 'Featured actions'
              : `Featured action ${slotNumber + 1}`
          }
          errorMessage={errorMessage}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.APPLE,
              passTab: PASS_TABS.FRONT,
              sectionId: FEATURED_ACTIONS_SECTION_ID,
            })
          }
        />
      );
    }

    if (error.startsWith('images.')) {
      const parts = error.split('.');
      const imageField = parts[2];
      const imageTabTitle = parts[1];
      const isDimensionError = parts.length === 4 && parts[3] === 'dimensions';
      const tabMap: Record<string, BuilderDeviceTab> = {
        apple: TABS.APPLE,
        google: TABS.GOOGLE,
        shared: TABS.SHARED,
      };
      const isStampBanner = imageField && imageField in STAMP_BANNER_LABELS;
      const imageLabel = STAMP_BANNER_LABELS[imageField]
        ? `${STAMP_BANNER_LABELS[imageField]} image`
        : `${capitalizeFirst(imageField)} image`;
      return (
        <ErrorLink
          key={error}
          label={imageLabel}
          errorMessage={
            isDimensionError
              ? `${isStampBanner ? errorMessage.replace(/^Image /, '') : errorMessage} (${capitalizeFirst(imageTabTitle)} view)`
              : `is required (${capitalizeFirst(imageTabTitle)} view)`
          }
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: tabMap[imageTabTitle] || TABS.SHARED,
              passTab: PASS_TABS.FRONT,
              sectionId: isStampBanner ? 'banners-section' : 'design-section',
            })
          }
        />
      );
    }

    // Colors
    if (error.includes('colors')) {
      const colorField = error.split('.')[1];
      const colorFieldLabel = capitalizeFirst(camelCaseToTitleCase(colorField));
      return (
        <ErrorLink
          key={error}
          label={colorFieldLabel}
          errorMessage="must be a valid hex color"
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FRONT,
              sectionId: 'design-section',
            })
          }
        />
      );
    }

    // Localisation
    if (error.includes('localisation')) {
      const parts = error.split('.');
      const section = parts[1];
      const slot = parts[2];
      return (
        <ErrorLink
          key={error}
          label={`${capitalizeFirst(section)} ${slot} translation`}
          errorMessage={errorMessage?.toLowerCase() || 'is incomplete'}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FEATURES,
              sectionId: 'languages-section',
            })
          }
        />
      );
    }

    // Loyalty balance default value
    if (error === 'loyaltyBalance.defaultValue') {
      return (
        <ErrorLink
          key={error}
          label="Loyalty balance"
          errorMessage="must be a number"
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FRONT,
              sectionId: 'fields-section',
            })
          }
        />
      );
    }

    // Fields
    if (error.includes('fields')) {
      const parts = error.split('.');
      const field = parts[1];
      const slot = parts[2];
      const fieldLabel =
        expressivePassRolloutEnabled && field === 'back'
          ? `Additional ${slot}`
          : `${capitalizeFirst(field)} ${slot}`;
      const fieldProp = parts[3];
      let fieldMessage: string;
      if (errorMessage === 'Field ID is required') {
        fieldMessage = 'field id is required';
      } else if (errorMessage?.includes('https://')) {
        fieldMessage = 'URL must start with https://, http://, mailto: or tel:';
      } else {
        fieldMessage = 'has duplicate field id';
      }
      const focusField =
        fieldProp === 'defaultValue'
          ? `${field}.${slot}.defaultValue`
          : `${field}.${slot}.id`;
      const passTab = field === 'back' ? PASS_TABS.BACK : PASS_TABS.FRONT;
      return (
        <ErrorLink
          key={error}
          label={fieldLabel}
          errorMessage={fieldMessage}
          onNavigate={() => {
            dispatch(setFocusedField(focusField));
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab,
              sectionId: 'fields-section',
            });
          }}
        />
      );
    }

    // Stamp card short/long values (updated text)
    if (
      (error.startsWith('stampShortValues.') ||
        error.startsWith('stampLongValues.')) &&
      errorMessage === 'Updated field text is required'
    ) {
      const fieldKey = error.split('.')[1];
      const stampLabel = STAMP_VALUE_LABELS[fieldKey] ?? fieldKey;
      return (
        <ErrorLink
          key={error}
          label={stampLabel}
          errorMessage="updated field text is required"
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FRONT,
              sectionId: 'field-updates-section',
            })
          }
        />
      );
    }

    // NFC
    if (error.includes('nfc')) {
      return (
        <ErrorLink
          key={error}
          label="NFC"
          errorMessage={errorMessage}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FEATURES,
              sectionId: 'nfc-qr-section',
            })
          }
        />
      );
    }

    // Barcode
    if (error.includes('barcode')) {
      return (
        <ErrorLink
          key={error}
          label="Barcode"
          errorMessage={errorMessage}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.SHARED,
              passTab: PASS_TABS.FEATURES,
              sectionId: 'nfc-qr-section',
            })
          }
        />
      );
    }

    // App linking
    if (error.includes('appLinking')) {
      const type = error.split('.')[1];
      const typeLabel = capitalizeFirst(camelCaseToTitleCase(type));
      const tab = type === 'appleAppStoreUrl' ? TABS.APPLE : TABS.GOOGLE;
      return (
        <ErrorLink
          key={error}
          label={typeLabel}
          errorMessage={errorMessage.toLowerCase()}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab,
              passTab: PASS_TABS.FEATURES,
              sectionId: 'app-linking-section',
            })
          }
        />
      );
    }

    // Beacons
    if (error.includes('beacons')) {
      const beaconIndex = parseInt(error.split('.')[1]);
      return (
        <ErrorLink
          key={error}
          label={`Beacon ${beaconIndex + 1}`}
          errorMessage={errorMessage}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: TABS.APPLE,
              passTab: PASS_TABS.FEATURES,
              sectionId: 'beacons-section',
            })
          }
        />
      );
    }

    // Locations
    if (error.includes('locations')) {
      const parts = error.split('.');
      const locationIndex = parseInt(parts[1] || '0', 10) || 0;
      const field = parts[2];
      const tabForError = field === 'message' ? TABS.APPLE : TABS.SHARED;
      const locationBannerMessage =
        field === 'name' && typeof errorMessage === 'string'
          ? errorMessage.replace(/^Location\s+/i, '')
          : errorMessage;
      return (
        <ErrorLink
          key={error}
          label={`Location ${locationIndex + 1}`}
          errorMessage={locationBannerMessage}
          onNavigate={() =>
            navigate({
              mainTabIndex: MAIN_TABS.PASS_TEMPLATE,
              tab: tabForError,
              passTab: PASS_TABS.FEATURES,
              sectionId: 'locations-section',
              expandLocationIndex: locationIndex,
            })
          }
        />
      );
    }

    // Fallback for unhandled errors
    return <li key={error}>{errorMessage}</li>;
  };

  return (
    <Box
      bg="#FFDBDB"
      w="full"
      mt={4}
      py={2}
      px={4}
      borderLeft="10px solid #FF1010"
    >
      <Flex direction="row" gap={3} alignItems="center">
        <CustomErrorIcon boxSize="20px" color="#FF1010" />
        <Text textStyle="bodyFamilySemiBold" fontSize="12px">
          {errorCount} error{errorCount > 1 ? 's' : ''} found
        </Text>
      </Flex>
      <Flex direction="column" pl={10}>
        <Text textStyle="bodyFamilyRegular" fontSize="12px">
          <ul>{Object.keys(displayedErrors).map(renderErrorItem)}</ul>
        </Text>
      </Flex>
    </Box>
  );
};

export default ErrorsBanner;
